import {
  Box,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import { useTranslation } from 'react-i18next';
import { StyledInputLabel } from '../../../../components/inputs/InputLabel';
import {
  MultiSelectChips,
  type MultiSelectOption,
} from '../../../../components/inputs/MultiSelectChips';
import { SearchField } from '../../../../components/inputs/SearchField';
import { AgreementIsActive } from '../../../../types/Agreements.types';
import { statusColors } from './StatusColors';

type AgreementsFiltersProps = {
  publisherFilter: string;
  onPublisherFilterChange: (value: string) => void;
  statusFilter: AgreementIsActive[];
  onStatusFilterChange: (value: AgreementIsActive[]) => void;
  expiringSoonFilter: boolean;
  onExpiringSoonFilterChange: (value: boolean) => void;
};

const statusOptions: MultiSelectOption[] = Object.values(AgreementIsActive).map(
  (status) => ({
    value: status,
    label: status.charAt(0).toUpperCase() + status.slice(1),
    backgroundColor: statusColors[status],
  })
);

export const AgreementsFilters = ({
  publisherFilter,
  onPublisherFilterChange,
  statusFilter,
  onStatusFilterChange,
  expiringSoonFilter,
  onExpiringSoonFilterChange,
}: AgreementsFiltersProps) => {
  const { t } = useTranslation();

  const handleStatusChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    onStatusFilterChange(
      (typeof value === 'string'
        ? value.split(',')
        : value) as AgreementIsActive[]
    );
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 16,
        alignItems: 'flex-end',
        mt: 16,
        p: 16,
        backgroundColor: 'var(--color-white)',
        borderRadius: 1,
      }}
    >
      <Box sx={{ width: { xs: '100%', lg: '33%' } }}>
        <StyledInputLabel htmlFor="publisher-filter">
          {t('agreements.filters.publisher', 'Publisher')}
        </StyledInputLabel>
        <SearchField
          id="publisher-filter"
          value={publisherFilter}
          onChange={onPublisherFilterChange}
          placeholder={
            t(
              'agreements.filters.publisherPlaceholder',
              'Search for publisher'
            ) as string
          }
          ariaLabel={t('agreements.filters.publisherPlaceholder', 'Search for publisher') as string}
          width="full"
        />
      </Box>
      <Box sx={{ width: { xs: '100%', lg: '33%' } }}>
        <StyledInputLabel id="status-filter-label" htmlFor="status-filter-input">
          {t('agreements.filters.status', 'Filter by status')}
        </StyledInputLabel>
        <MultiSelectChips
          id="status-filter"
          name="status-filter"
          labelId="status-filter-label"
          value={statusFilter}
          options={statusOptions}
          onChange={handleStatusChange}
        />
      </Box>
      <FormControlLabel
        sx={{ width: { xs: '100%', lg: 'auto' } }}
        control={
          <Checkbox
            checked={expiringSoonFilter}
            onChange={(e) => onExpiringSoonFilterChange(e.target.checked)}
          />
        }
        label={t('agreements.filters.expiringSoon', 'Expiring within 60 days')}
      />
    </Box>
  );
};
